import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Lock, BookOpen, ArrowRight, Award } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { TRAIN } from "@/constants/testIds";

export default function TrackPage({ trackKey: fixed }) {
  const params = useParams();
  const trackKey = fixed || params.trackKey;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null); setErr("");
    api.get(`/training/tracks/${trackKey}`)
       .then((r) => setData(r.data))
       .catch((e) => setErr(apiErrorMessage(e)));
  }, [trackKey]);

  if (err) {
    return (
      <div className="glass-panel p-10 max-w-xl mx-auto text-center">
        <div className="eyebrow mb-2">Track locked</div>
        <h2 className="font-anton text-3xl">{err}</h2>
        <p className="text-lifted-dim mt-3">Ask a master admin to grant the appropriate role.</p>
        <Link to="/training" className="btn-ghost mt-6 inline-flex">← Back to training hub</Link>
      </div>
    );
  }
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>;

  const completed = data.modules.filter((m) => m.progress?.completed).length;
  const total = data.modules.length;
  const allDone = total > 0 && completed === total;
  const cert = data.certification;

  return (
    <div className="space-y-10">
      <header className="fade-up">
        <div className="eyebrow">{trackKey.toUpperCase()} TRACK</div>
        <h1 className="headline-xl">{data.track.title}</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">{data.track.description}</p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Counter label="Modules" value={`${completed} / ${total}`} />
        <Counter label="Passing score" value={`${data.track.passing_score}%`} />
        <Counter label="Status" value={cert ? "CERTIFIED" : allDone ? "READY" : "IN PROGRESS"} highlight={!!cert} />
      </section>

      <section data-testid={TRAIN.modulesGrid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.modules.map((m, i) => (
          <button
            key={m.id}
            data-testid={TRAIN.moduleCard(m.id)}
            onClick={() => navigate(`/module/${trackKey}/${m.number}`)}
            className={`glass-panel p-5 text-left fade-up delay-${(i % 5) + 1} hover:-translate-y-1 hover:border-lifted-green/40 transition-transform`}
          >
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 rounded-lg border border-lifted-green/30 bg-lifted-green/10 text-lifted-green flex items-center justify-center font-anton">
                {m.number}
              </div>
              {m.progress?.completed
                ? <span className="chip chip--green">Done</span>
                : <span className="chip">{m.progress?.completion_pct || 0}%</span>}
            </div>
            <div className="font-anton text-lg uppercase mt-4">{m.title}</div>
            <p className="text-xs text-lifted-dim mt-1.5 line-clamp-2">{m.description}</p>
            <div className="mt-4 flex items-center justify-between font-bebas tracking-[0.18em] text-[0.7rem] text-lifted-mute">
              <span><BookOpen className="h-3 w-3 inline-block mr-1" />{m.subcategories?.length || 0} parts · {m.duration_minutes}m</span>
              <ArrowRight className="h-3 w-3 text-lifted-dim" />
            </div>
          </button>
        ))}
      </section>

      <section className="glass-panel p-6 fade-up">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Award className="h-3 w-3" /> Certification</div>
            <h2 className="font-anton text-2xl uppercase mt-1">
              {cert ? "You're certified" : allDone ? "Ready to certify" : "Complete all modules to unlock the test"}
            </h2>
            {cert && (
              <p className="text-sm text-lifted-dim mt-1">
                Verification code: <code data-testid={TRAIN.certVerificationCode} className="text-lifted-green">{cert.verification_code}</code> · Score {cert.score}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            {!cert && (
              <button
                disabled={!allDone}
                onClick={() => navigate(`/certification?track=${trackKey}`)}
                className="btn-lifted disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={TRAIN.certStartBtn(trackKey)}
              >
                {allDone ? "Start certification" : `Complete ${total - completed} more`}
              </button>
            )}
            <Link to="/certification" className="btn-ghost">View certifications</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Counter({ label, value, highlight = false }) {
  return (
    <div className="glass-panel p-5">
      <div className="eyebrow !text-lifted-text-dim">{label}</div>
      <div className={`mt-2 font-anton text-3xl ${highlight ? "text-lifted-green" : "text-lifted-text"}`}>{value}</div>
    </div>
  );
}
